/* eslint-disable no-restricted-imports */
import React, {useCallback, useEffect, useRef, useState} from 'react'
import Confetti from 'react-confetti'
import styles from './UIGameAchievement.module.scss'
import {GameAchievementType, getAchievementData} from './type'
import {GameClientUserState} from '@colonist/game-base-types'
import {audioManager, Sound} from '../../../../../code/client/shared/scripts/Sound'
import {Avatar} from '../../common/Avatar'
import {T} from '../../common/T'
import clsx from 'clsx'
import {sleep} from '../../utils/helpers'
import {GameModeTranslationKeyContext, getNestedTranslation, TranslationObject} from '@colonist/i18n'
import IconTradeArrowGreen from '@static/images/icon_trade_arrow_green.svg'

const getVPChangeText = (increase: boolean = true, victoryPointChange?: number): TranslationObject | null => {
    if(victoryPointChange == null || victoryPointChange === 0) {
        return null
    }

    const operationSign = increase ? '+' : '-'
    return getNestedTranslation({
        key: 'strings:colonistCommon.utils.#append',
    }, {
        value1: {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: operationSign,
            },
        },
        value2: {
            key: 'strings:game.playerInformation.victoryPoint.countAbbreviated',
            options: {
                count: victoryPointChange,
            },
        },
    })
}

const DISAPPEAR_ANIMATION_DURATION = 1000

export interface UIGameAchievementProps {
    durationMs?: number
    isConfettiEnabled: boolean
    achievementOwnerNew: GameClientUserState
    achievementOwnerPrevious?: GameClientUserState | undefined
    achievementType: GameAchievementType
    achievementScore?: number | undefined
    translationContext: GameModeTranslationKeyContext
    onDisappear: () => void
    customAudio?: Sound.SoundType
}

export const UIGameAchievement = ({
    durationMs = 5000,
    onDisappear,
    isConfettiEnabled,
    achievementOwnerNew,
    achievementOwnerPrevious,
    achievementType,
    achievementScore,
    translationContext,
    customAudio,
}: UIGameAchievementProps) => {
    const achievementData = getAchievementData(achievementType, achievementOwnerNew.selectedColor, translationContext)
    const [isHidden, setIsHidden] = useState<boolean>(false)
    const isDisappearingRef = useRef<boolean>(false)

    const triggerDisappear = useCallback(async() => {
        if(isDisappearingRef.current) return

        isDisappearingRef.current = true
        setIsHidden(true)
        await sleep(DISAPPEAR_ANIMATION_DURATION)
        onDisappear()
    }, [onDisappear])

    const runAnimation = async() => {
        if(customAudio != null) {
            audioManager.play(customAudio)
        } else if(achievementData.soundType != null) {
            audioManager.play(achievementData.soundType)
        }
        await sleep(durationMs)
        void triggerDisappear()
    }
    useEffect(() => {
        void runAnimation()
    }, [])

    const handleClick = useCallback(() => {
        void triggerDisappear()
    }, [triggerDisappear])

    const renderAchievementSubtitle = () => {
        if(achievementData.subTitle) {
            return (
                <div className={styles.subTitle}>
                    <T id={achievementData.subTitle}/>
                </div>
            )
        }

        const vpChangeText = getVPChangeText(true, achievementData.victoryPointChange)
        const hasVPChange = vpChangeText != null
        if(achievementOwnerPrevious != null) {
            const vpChangeTextPrevious = getVPChangeText(false, achievementData.victoryPointChange)
            const hasVPChangePrevious = vpChangeTextPrevious != null
            return <div className={clsx(styles.pointChanges, styles.withPreviousOwner)}>
                <Avatar
                    className={styles.avatar}
                    color={achievementOwnerPrevious.selectedColor}
                    image={achievementOwnerPrevious.databaseIcon}
                />
                <div className={styles.playerPointChange}>
                    <span className={styles.playerName}>{achievementOwnerPrevious.username}</span>
                    {hasVPChangePrevious && <span className={clsx(styles.points, styles.negative)}>
                        <T id={vpChangeTextPrevious}/>
                    </span>}
                </div>

                <img className={styles.transferIcon} src={IconTradeArrowGreen} />

                <Avatar
                    className={styles.avatar}
                    color={achievementOwnerNew.selectedColor}
                    image={achievementOwnerNew.databaseIcon}
                />
                <div className={styles.playerPointChange}>
                    <span className={styles.playerName}>{achievementOwnerNew.username}</span>
                    {hasVPChange && <span className={styles.points}>
                        <T id={vpChangeText}/>
                    </span>}
                </div>
            </div>
        }

        return <div className={styles.pointChanges}>
            <Avatar
                className={styles.avatar}
                color={achievementOwnerNew.selectedColor}
                image={achievementOwnerNew.databaseIcon}
            />
            <span className={styles.playerName}>{achievementOwnerNew.username}</span>
            {hasVPChange && <span className={styles.points}>
                <T id={vpChangeText}/>
            </span>}
        </div>
    }

    const showAchievementScore = achievementScore != null
    return (
        <div
            className={clsx(styles.container, {
                [styles.hidden]: isHidden})}
            style={{transitionDuration: `${DISAPPEAR_ANIMATION_DURATION}ms`}}
            onClick={handleClick}>

            {isConfettiEnabled && <Confetti
                className={styles.confetti}
                initialVelocityX={10}
                initialVelocityY={-10}
                numberOfPieces={175}
                recycle={false} />}

            <div className={styles.header}>
                <T id={achievementData.title}/>
                <div className={styles.iconWrapper}>
                    <img src={achievementData.icon} />
                    {achievementData.subIcon != null && <img className={styles.subIcon} src={achievementData.subIcon} />}
                    {showAchievementScore && <span className={styles.point}>{achievementScore}</span>}
                </div>
            </div>

            {renderAchievementSubtitle()}
        </div>
    )
}

export default UIGameAchievement
